import React from 'react';
import { currentVersionsOf, kindOf } from '../quoteChains.js';
const { useMemo } = React;

import { PRICING_CONFIG, detectCountry } from '../pricing.js';

// Regions for grouping
const REGION = {
  usa: 'North America', canada: 'North America',
  uk: 'Europe', spain: 'Europe',
  mexico: 'Latin America', chile: 'Latin America',
  uruguay: 'Latin America', argentina: 'Latin America', bolivia: 'Latin America',
};

function countryLabel(key) {
  return PRICING_CONFIG.countries[key]?.label || key;
}

function fmtMoney(n, currency) {
  if (!n) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(n);
  } catch { return `${currency} ${Number(n).toLocaleString()}`; }
}

function avg(arr) {
  const nums = arr.filter(n => n > 0);
  return nums.length ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : 0;
}

export default function SolutionsHub({ data }) {
  const { accounts = [] } = data;
  // Company quotes only, one version per quote (never every revision; personal quotes are not company pricing)
  const diagnosticIntakes = useMemo(() => currentVersionsOf(data.diagnosticIntakes || []).filter(r => kindOf(r.fields || {}) === 'company'), [data.diagnosticIntakes]);

  const { byService, byCountry, summary } = useMemo(() => {
    // serviceName → { count, onceCount, monthlyCount, onceFees[], monthlyFees[], countries: Map<key,currency> }
    const serviceMap = {};
    // countryKey → { label, region, currency, serviceNames: Set, onceFees[], monthlyFees[], recurringCount }
    const countryMap = {};

    for (const intake of diagnosticIntakes) {
      const f = intake.fields || {};
      const currency   = f['Quote Currency'] || 'USD';

      // Prefer Quote Market (saved at quote time); fall back to account's Country field
      let countryKey = f['Quote Market'] || null;
      if (!countryKey) {
        const accId = Array.isArray(f['Account']) ? f['Account'][0] : null;
        const acc = accId ? accounts.find(a => a.id === accId) : null;
        const countryText = acc?.fields?.['Country'] || '';
        if (countryText) countryKey = detectCountry(countryText);
      }

      let lines;
      try { lines = JSON.parse(f['Quote Lines JSON'] || '[]'); } catch { lines = []; }

      for (const line of lines) {
        const name    = (line.service || '').trim();
        const once    = Number(line.onceFee)    || 0;
        const monthly = Number(line.monthlyFee) || 0;
        if (!name || (!once && !monthly)) continue;

        // ── by service ──
        if (!serviceMap[name]) {
          serviceMap[name] = { count: 0, onceCount: 0, monthlyCount: 0, onceFees: [], monthlyFees: [], countries: new Map() };
        }
        serviceMap[name].count++;
        if (once)    { serviceMap[name].onceCount++;    serviceMap[name].onceFees.push(once); }
        if (monthly) { serviceMap[name].monthlyCount++; serviceMap[name].monthlyFees.push(monthly); }
        if (countryKey) serviceMap[name].countries.set(countryKey, currency);

        // ── by country ──
        if (countryKey) {
          if (!countryMap[countryKey]) {
            countryMap[countryKey] = {
              label: countryLabel(countryKey),
              region: REGION[countryKey] || 'Other',
              currency,
              serviceNames: new Set(),
              onceFees: [], monthlyFees: [],
              recurringCount: 0,
            };
          }
          countryMap[countryKey].serviceNames.add(name);
          if (once)    countryMap[countryKey].onceFees.push(once);
          if (monthly) { countryMap[countryKey].monthlyFees.push(monthly); countryMap[countryKey].recurringCount++; }
        }
      }
    }

    const byService = Object.entries(serviceMap)
      .map(([name, s]) => ({
        name,
        count:         s.count,
        onceCount:     s.onceCount,
        monthlyCount:  s.monthlyCount,
        avgOnce:       avg(s.onceFees),
        avgMonthly:    avg(s.monthlyFees),
        countries:     [...s.countries.entries()].map(([k, cur]) => ({ key: k, label: countryLabel(k), currency: cur })),
      }))
      .sort((a, b) => b.count - a.count);

    const byCountry = Object.entries(countryMap)
      .map(([key, c]) => ({
        key, label: c.label, region: c.region, currency: c.currency,
        services: c.serviceNames.size,
        avgOnce:  avg(c.onceFees),
        avgMonthly: avg(c.monthlyFees),
        recurringCount: c.recurringCount,
        onceCount: c.onceFees.length,
      }))
      .sort((a, b) => (b.onceCount + b.recurringCount) - (a.onceCount + a.recurringCount));

    // Group countries by region for the table
    const regionOrder = ['North America', 'Europe', 'Latin America', 'Other'];
    byCountry.sort((a, b) => regionOrder.indexOf(a.region) - regionOrder.indexOf(b.region) || a.label.localeCompare(b.label));

    const totalRecurring = byService.reduce((s, sv) => s + sv.monthlyCount, 0);
    const intakesWithQuote = diagnosticIntakes.filter(r => {
      try { return JSON.parse(r.fields?.['Quote Lines JSON'] || '[]').some(l => l.service?.trim()); } catch { return false; }
    }).length;

    return { byService, byCountry, summary: { intakesWithQuote, totalRecurring, uniqueServices: byService.length } };
  }, [diagnosticIntakes]);

  const isEmpty = byService.length === 0;

  // Group countries by region for display
  const regionGroups = useMemo(() => {
    const groups = {};
    for (const c of byCountry) {
      if (!groups[c.region]) groups[c.region] = [];
      groups[c.region].push(c);
    }
    return groups;
  }, [byCountry]);

  return (
    <div>
      <div className="page-header">
        <h1>Precios cotizados</h1>
        <p>Se arma solo a partir de las cotizaciones y se actualiza con cada empresa nueva.</p>
      </div>

      {!isEmpty && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
          {[
            { label: 'Empresas cotizadas',   value: summary.intakesWithQuote },
            { label: 'Servicios',          value: summary.uniqueServices },
            { label: 'Recurrentes activos',  value: summary.totalRecurring, green: true },
          ].map(({ label, value, green }) => (
            <div key={label} style={{ padding: '10px 18px', background: green ? 'rgba(52,211,153,0.08)' : 'rgba(91,191,181,0.08)',
              border: `1px solid ${green ? 'rgba(52,211,153,0.2)' : 'rgba(91,191,181,0.2)'}`, borderRadius: 10 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: green ? '#1E7B4F' : 'var(--globant-green)' }}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--globant-muted)', marginTop: 1 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {isEmpty ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--globant-muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🛠️</div>
          <div style={{ fontWeight: 600, color: 'var(--globant-text)', marginBottom: 6 }}>Todavía no hay cotizaciones</div>
          <div style={{ fontSize: 13 }}>Generá cotizaciones desde la pestaña Cotización de una empresa: esta vista se arma sola.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

          {/* ── By service ── */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--globant-muted)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 14 }}>
              By service
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {byService.map(svc => (
                <div key={svc.name} className="card" style={{ borderTop: `3px solid ${svc.monthlyCount > 0 ? '#1E7B4F' : 'var(--globant-green)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--globant-text)', flex: 1, lineHeight: 1.3 }}>{svc.name}</div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 10, whiteSpace: 'nowrap', marginLeft: 8,
                      background: 'rgba(91,191,181,0.1)', color: 'var(--globant-green)', border: '1px solid rgba(91,191,181,0.25)' }}>
                      {svc.count}× quoted
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                    {svc.avgOnce > 0 && (
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 2 }}>Promedio único</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--globant-text)' }}>
                          {svc.countries.length === 1
                            ? fmtMoney(svc.avgOnce, svc.countries[0].currency)
                            : `~${svc.avgOnce.toLocaleString()}`}
                        </div>
                        {svc.onceCount > 0 && (
                          <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginTop: 1 }}>{svc.onceCount} quote{svc.onceCount !== 1 ? 's' : ''}</div>
                        )}
                      </div>
                    )}
                    {svc.avgMonthly > 0 && (
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 2 }}>Promedio recurrente</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1E7B4F' }}>
                          {svc.countries.length === 1
                            ? fmtMoney(svc.avgMonthly, svc.countries[0].currency)
                            : `~${svc.avgMonthly.toLocaleString()}`}
                          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--globant-muted)' }}>/mes</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#1E7B4F', marginTop: 1, fontWeight: 600 }}>
                          {svc.monthlyCount} recurring
                        </div>
                      </div>
                    )}
                  </div>

                  {svc.countries.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {svc.countries.map(c => (
                        <span key={c.key} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8,
                          background: 'rgba(30,42,44,0.05)', color: 'var(--globant-muted)', border: '1px solid var(--globant-border)' }}>
                          {c.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ── By market ── */}
          {byCountry.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--globant-muted)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 14 }}>
                By market
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {Object.entries(regionGroups).map(([region, countries]) => (
                  <div key={region}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--globant-border)' }}>
                      {region}
                    </div>
                    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '8px 16px', background: 'var(--globant-darker)', borderBottom: '1px solid var(--globant-border)' }}>
                        {['Country', 'Services', 'Promedio único', 'Promedio recurrente', 'Recurring deals'].map(h => (
                          <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                        ))}
                      </div>
                      {countries.map((c, i) => (
                        <div key={c.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', padding: '12px 16px',
                          borderBottom: i < countries.length - 1 ? '1px solid var(--globant-border)' : 'none', alignItems: 'center' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--globant-text)' }}>{c.label}</div>
                          <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>{c.services}</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--globant-text)' }}>
                            {c.avgOnce > 0 ? fmtMoney(c.avgOnce, c.currency) : '—'}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1E7B4F' }}>
                            {c.avgMonthly > 0 ? `${fmtMoney(c.avgMonthly, c.currency)}/mo` : '—'}
                          </div>
                          <div style={{ fontSize: 12, color: c.recurringCount > 0 ? '#1E7B4F' : 'var(--globant-muted)', fontWeight: c.recurringCount > 0 ? 700 : 400 }}>
                            {c.recurringCount > 0 ? `${c.recurringCount} active` : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
